import React, { ComponentType, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { AuthPage } from '@/features/auth/pages/AuthPage';
import { EnhancedAuthProvider } from '@/features/auth/contexts/EnhancedAuthContext';
import { EnhancedProtectedRoute } from '@/features/auth/components/EnhancedProtectedRoute';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { TenantProvider } from '@/contexts/TenantContext';
import { GA4Provider } from '@/components/analytics/GA4Provider';
import { lazyWithRetry } from '@/lib/utils/lazyWithRetry';
import { useClearCachesOnSignOut } from '@/hooks/useClearCachesOnSignOut';

// Lazy load pages for code splitting and faster initial load
// Using lazyWithRetry to auto-reload on stale chunk errors after deployments
const CampaignsPage = lazyWithRetry(
  () => import('@/features/campaigns/pages/CampaignsPage'),
  'CampaignsPage'
);
const TechniciansPage = lazyWithRetry(
  () => import('@/features/technicians/pages/TechniciansPage'),
  'TechniciansPage'
);
import { Loader2 } from 'lucide-react';

// Lazy load DealersPage for code splitting and faster initial load
const DealersPage = lazyWithRetry(
  () => import('@/features/dealers/pages/DealersPage'),
  'DealersPage'
);

// Lazy load API documentation page
const ApiDocsPage = lazyWithRetry(
  () =>
    import('@/pages/ApiDocsPage').then(module => ({
      default: module.ApiDocsPage as ComponentType<unknown>
    })),
  'ApiDocsPage'
);

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className='flex items-center justify-center py-12'>
    <Loader2 className='h-8 w-8 animate-spin' />
    <span className='ml-2'>Loading...</span>
  </div>
);
import RecallListPage from '@/features/recalls/pages/RecallListPage';
import SimpleRecallMapPage from '@/features/recalls/pages/SimpleRecallMapPage';
import ManualRoutingPage from '@/features/routing/pages/ManualRoutingPage';
import UnifiedRoutingPage from '@/features/routing/pages/UnifiedRoutingPage';
import RouteConfirmationPage from '@/features/routing/pages/RouteConfirmationPage';
import RouteTrackerPageV3 from '@/features/routing/pages/RouteTrackerPageV3';
import RoutePreviewPage from '@/features/routing/pages/RoutePreviewPage';
const DailyRouteSummaryPage = lazyWithRetry(
  () => import('@/features/routing/pages/DailyRouteSummaryPage'),
  'DailyRouteSummaryPage'
);
import LandingPage from '@/features/dashboard/pages/LandingPage';
import { OnboardingFlow } from '@/features/profile/pages/OnboardingFlow';
import { NotificationPreferences } from '@/features/profile/pages/NotificationPreferences';
import PerformanceTrackingPage from '@/features/performance/pages/PerformanceTrackingPage';
// import UserApprovalsPage from '@/features/auth/pages/UserApprovalsPage';
import ControlCenterPage from '@/features/admin/pages/ControlCenterPage';
import FeedbackAdminPage from '@/features/feedback/pages/FeedbackAdminPage';

const AnalyticsPage = lazyWithRetry(
  () =>
    import('@/features/admin/pages/AnalyticsPage').then(module => ({
      default: module.default as ComponentType<unknown>
    })),
  'AnalyticsPage'
);
import { AuthCallback } from '@/features/auth/components/AuthCallback';
import { SetPasswordPage } from '@/features/auth/pages/SetPasswordPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { ResetPasswordConfirmPage } from '@/features/auth/pages/ResetPasswordConfirmPage';
import { InvitePage } from '@/features/auth/pages/InvitePage';
import { InviteConfirmPage } from '@/features/auth/pages/InviteConfirmPage';
import { AccountSetPasswordPage } from '@/features/auth/pages/AccountSetPasswordPage';
import { Toaster } from '@/components/ui/toaster';
import {
  DocsHomePage,
  GettingStartedPage,
  FeaturesIndexPage,
  FiltersGuidePage,
  RecallMapGuidePage,
  RecallListGuidePage,
  ManualRoutingGuidePage,
  RoutePreviewGuidePage,
  RouteTrackerGuidePage,
  DarkModeGuidePage,
  CampaignsGuidePage,
  DealersGuidePage,
  TechniciansGuidePage,
  ProfileSettingsGuidePage,
  GuidesIndexPage,
  SupportPage,
  PrivacyPolicyPage,
  TermsOfServicePage,
  AcceptableUsePolicyPage,
  SmartFeaturesPage,
  CookiePolicyPage,
  DataProcessingAgreementPage,
  ServiceProvidersPage,
  SecurityPolicyPage
} from '@/features/docs/pages';

// eslint-disable-next-line max-lines-per-function
const App: React.FC = () => {
  useClearCachesOnSignOut();

  return (
    <ThemeProvider>
      <EnhancedAuthProvider>
        <TenantProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <GA4Provider>
              <Toaster />
              <Routes>
                {/* Auth route - no protection needed */}
                <Route path='/auth' element={<AuthPage />} />
                <Route path='/auth/callback' element={<AuthCallback />} />
                <Route path='/auth/set-password' element={<SetPasswordPage />} />
                <Route path='/auth/reset-password' element={<ResetPasswordPage />} />
                <Route path='/auth/reset-password/confirm' element={<ResetPasswordConfirmPage />} />
                <Route path='/auth/invite' element={<InvitePage />} />
                {/* OTP-based invite flow (scanner-safe) */}
                <Route path='/invite/confirm' element={<InviteConfirmPage />} />
                <Route path='/account/set-password' element={<AccountSetPasswordPage />} />

                {/* Main application with enhanced authentication */}
                <Route
                  path='/'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <LandingPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />

                {/* Management Screens with role-based access */}
                <Route
                  path='/campaigns'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'campaigns', action: 'read' }]}
                    >
                      <Layout>
                        <Suspense fallback={<PageLoader />}>
                          <CampaignsPage />
                        </Suspense>
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/technicians'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'technicians', action: 'read' }]}
                    >
                      <Layout>
                        <Suspense fallback={<PageLoader />}>
                          <TechniciansPage />
                        </Suspense>
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/dealers'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'dealers', action: 'read' }]}
                    >
                      <Layout>
                        <Suspense fallback={<PageLoader />}>
                          <DealersPage />
                        </Suspense>
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />

                {/* Recall Management Screens */}
                <Route
                  path='/recalls'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'recalls', action: 'read' }]}
                    >
                      <Layout>
                        <RecallListPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/recall-map'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'recalls', action: 'read' }]}
                    >
                      <Layout>
                        <SimpleRecallMapPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />

                {/* Technician Assignment - New Unified Page */}
                <Route
                  path='/routing/manual'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'recalls', action: 'read' }]}
                    >
                      <Layout>
                        <UnifiedRoutingPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                {/* Legacy Technician Assignment (kept for reference) */}
                <Route
                  path='/routing/manual-legacy'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'recalls', action: 'assign_technicians' }]}
                    >
                      <Layout>
                        <ManualRoutingPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/routing/confirmation'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'recalls', action: 'assign_technicians' }]}
                    >
                      <Layout>
                        <RouteConfirmationPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/routing/preview'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'recalls', action: 'read' }]}
                    >
                      <Layout>
                        <RoutePreviewPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />

                {/* Performance Tracking - Step 3 of workflow */}
                <Route
                  path='/performance'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'recalls', action: 'read' }]}
                    >
                      <Layout>
                        <PerformanceTrackingPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />

                {/* Route Tracker - Operational route tracking */}
                <Route
                  path='/route-tracker'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'recalls', action: 'read' }]}
                    >
                      <Layout>
                        <RouteTrackerPageV3 />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />

                {/* Daily Route Summary - per-tech, per-day printable */}
                <Route
                  path='/routing/summary'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'recalls', action: 'read' }]}
                    >
                      <Layout>
                        <Suspense fallback={<PageLoader />}>
                          <DailyRouteSummaryPage />
                        </Suspense>
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />

                <Route
                  path='/onboarding'
                  element={
                    <EnhancedProtectedRoute>
                      <OnboardingFlow />
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/profile/notifications'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <NotificationPreferences />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />

                {/* Control Center - Super Admin only */}
                <Route
                  path='/admin/control-center'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'users', action: 'manage' }]}
                    >
                      <Layout>
                        <ControlCenterPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />

                {/* Analytics - Super Admin only */}
                <Route
                  path='/admin/analytics'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'users', action: 'manage' }]}
                    >
                      <Layout>
                        <Suspense fallback={<PageLoader />}>
                          <AnalyticsPage />
                        </Suspense>
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />

                {/* Feedback Admin - Admin only */}
                <Route
                  path='/admin/feedback'
                  element={
                    <EnhancedProtectedRoute
                      requiredPermissions={[{ resource: 'users', action: 'manage' }]}
                    >
                      <Layout>
                        <FeedbackAdminPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />

                {/* API Documentation - Super Admin only */}
                <Route
                  path='/api'
                  element={
                    <EnhancedProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <ApiDocsPage />
                      </Suspense>
                    </EnhancedProtectedRoute>
                  }
                />

                {/* Documentation - Accessible to all authenticated users */}
                <Route
                  path='/docs'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <DocsHomePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/getting-started'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <GettingStartedPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/features'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <FeaturesIndexPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/features/filters'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <FiltersGuidePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/features/recall-map'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <RecallMapGuidePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/features/recall-list'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <RecallListGuidePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/features/manual-routing'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <ManualRoutingGuidePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/features/route-preview'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <RoutePreviewGuidePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/features/route-tracker'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <RouteTrackerGuidePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/features/dark-mode'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <DarkModeGuidePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/features/campaigns'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <CampaignsGuidePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/features/dealers'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <DealersGuidePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/features/technicians'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <TechniciansGuidePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/features/profile'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <ProfileSettingsGuidePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/guides'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <GuidesIndexPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/support'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <SupportPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/legal/privacy'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <PrivacyPolicyPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/legal/terms'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <TermsOfServicePage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/legal/acceptable-use'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <AcceptableUsePolicyPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/legal/smart-features'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <SmartFeaturesPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/legal/cookies'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <CookiePolicyPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/legal/dpa'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <DataProcessingAgreementPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/legal/service-providers'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <ServiceProvidersPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />
                <Route
                  path='/docs/legal/security'
                  element={
                    <EnhancedProtectedRoute>
                      <Layout>
                        <SecurityPolicyPage />
                      </Layout>
                    </EnhancedProtectedRoute>
                  }
                />

                {/* Catch all route - redirect to home */}
                <Route path='*' element={<Navigate to='/' replace />} />
              </Routes>
            </GA4Provider>
          </Router>
        </TenantProvider>
      </EnhancedAuthProvider>
    </ThemeProvider>
  );
};

export default App;
